import { ref, onMounted, onUnmounted } from 'vue';

export const useSlotManager = (accountId, userId) => {
  const storageKey = `agentMode:${accountId}:${userId}`;

  // Load slots immediately at initialization to ensure they're available
  // before any computed properties are evaluated
  const loadInitialSlots = () => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const slots = ref(loadInitialSlots());

  const loadSlotsFromStorage = () => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        slots.value = JSON.parse(stored);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load slots from localStorage:', error);
    }
  };

  const saveSlotsToStorage = () => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(slots.value));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save slots to localStorage:', error);
    }
  };

  const trimTrailingEmptySlots = () => {
    while (
      slots.value.length > 0 &&
      slots.value[slots.value.length - 1].conversationId === null
    ) {
      slots.value.pop();
    }
  };

  const getSlots = conversations => {
    const conversationIds = new Set(conversations.map(conv => conv.id || conv));

    // Clear conversationId for slots whose conversation no longer exists (keep slot visible)
    slots.value = slots.value.map(slot => ({
      ...slot,
      conversationId: conversationIds.has(slot.conversationId)
        ? slot.conversationId
        : null,
    }));

    // Get all currently slotted conversation IDs (excluding empty slots)
    const slottedIds = new Set(
      slots.value.map(slot => slot.conversationId).filter(id => id !== null)
    );

    // Find unslotted conversations
    const unslottedConversations = conversations.filter(
      conv => !slottedIds.has(conv.id || conv)
    );

    // Assign unslotted conversations to available slots (reuse empty slots first)
    unslottedConversations.forEach(conv => {
      const conversationId = conv.id || conv;

      // Find first empty slot (slot with null conversationId)
      const emptySlot = slots.value.find(s => s.conversationId === null);

      if (emptySlot) {
        // Reuse empty slot
        emptySlot.conversationId = conversationId;
      } else {
        // Find first available slot number
        const occupiedSlots = new Set(slots.value.map(s => s.slotNumber));
        let slotNumber = 1;

        while (occupiedSlots.has(slotNumber)) {
          slotNumber += 1;
        }

        slots.value.push({ slotNumber, conversationId });
      }
    });

    // Sort slots by slot number
    slots.value.sort((a, b) => a.slotNumber - b.slotNumber);

    trimTrailingEmptySlots();
    saveSlotsToStorage();

    return slots.value;
  };

  const assignToSlot = conversationId => {
    // Check if conversation already has a slot
    const existingSlot = slots.value.find(
      slot => slot.conversationId === conversationId
    );
    if (existingSlot) {
      return existingSlot.slotNumber;
    }

    // Find first empty slot (slot with null conversationId)
    const emptySlot = slots.value.find(s => s.conversationId === null);

    if (emptySlot) {
      // Reuse empty slot
      emptySlot.conversationId = conversationId;
      saveSlotsToStorage();
      return emptySlot.slotNumber;
    }

    // Find first available slot number
    const occupiedSlots = new Set(slots.value.map(s => s.slotNumber));
    let slotNumber = 1;

    while (occupiedSlots.has(slotNumber)) {
      slotNumber += 1;
    }

    // Assign to slot
    slots.value.push({ slotNumber, conversationId });

    // Sort slots by slot number
    slots.value.sort((a, b) => a.slotNumber - b.slotNumber);

    saveSlotsToStorage();

    return slotNumber;
  };

  const removeFromSlot = conversationId => {
    const slotIndex = slots.value.findIndex(
      slot => slot.conversationId === conversationId
    );

    if (slotIndex !== -1) {
      slots.value[slotIndex] = {
        ...slots.value[slotIndex],
        conversationId: null,
      };
      trimTrailingEmptySlots();
      saveSlotsToStorage();
    }
  };

  const handleStorageChange = event => {
    if (event.key === storageKey && event.newValue !== event.oldValue) {
      loadSlotsFromStorage();
    }
  };

  onMounted(() => {
    window.addEventListener('storage', handleStorageChange);
  });

  onUnmounted(() => {
    window.removeEventListener('storage', handleStorageChange);
  });

  return {
    slots,
    getSlots,
    assignToSlot,
    removeFromSlot,
  };
};
